import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Coins, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import { toast } from 'sonner';

/**
 * Modal that opens when:
 *   • User explicitly clicks "Top up" anywhere, OR
 *   • Any API call returns 402 Payment Required (intercepted by CreditsContext)
 *
 * One click on a pack creates a Checkout Session and redirects to Stripe.
 */
export default function BuyCreditsModal() {
  const { modalState, closeBuyModal } = useCredits();
  const { open, detail } = modalState;
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyPack, setBusyPack] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/billing/packs');
        setPacks(res.data.packs || []);
      } catch (_) {
        toast.error('Could not load credit packs');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handlePurchase = async (packId) => {
    setBusyPack(packId);
    try {
      const res = await api.post('/billing/checkout', {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Checkout URL missing — please try again.');
    } catch (_) {
      toast.error('Could not start checkout — please try again.');
    } finally {
      setBusyPack(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeBuyModal()}>
      <DialogContent className="max-w-2xl" data-testid="buy-credits-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-600" />
            Top up your credits
          </DialogTitle>
          <DialogDescription>
            Credits are the unit of value across the platform — every analysis,
            prognosis and spreadsheet upload deducts a small amount. One-time
            purchase, no subscription.
          </DialogDescription>
        </DialogHeader>

        {detail?.error === 'insufficient_credits' && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="buy-credits-deficit"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              <strong>{detail.feature}</strong> needs{' '}
              <strong>{detail.required} cr</strong>; you have{' '}
              <strong>{detail.available} cr</strong>. Pick a pack below.
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="animate-spin h-5 w-5" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3 mt-2">
            {packs.map((p) => (
              <div
                key={p.id}
                data-testid={`credit-pack-${p.id}`}
                className={`relative flex flex-col rounded-xl border p-4 ${
                  p.badge
                    ? 'border-amber-300 bg-amber-50 ring-2 ring-amber-200'
                    : 'border-slate-200 bg-white'
                }`}
              >
                {p.badge && (
                  <span className="absolute -top-2 right-4 inline-flex items-center rounded-full bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5">
                    {p.badge}
                  </span>
                )}
                <div className="text-xs text-slate-500 uppercase tracking-wide">{p.name}</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">
                  {p.credits.toLocaleString()} cr
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  ${p.price_usd.toFixed(0)}
                </div>
                {p.tagline && (
                  <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">{p.tagline}</p>
                )}
                <Button
                  className="mt-3 w-full"
                  onClick={() => handlePurchase(p.id)}
                  disabled={busyPack !== null}
                  data-testid={`buy-pack-${p.id}`}
                >
                  {busyPack === p.id ? (
                    <Loader2 className="animate-spin h-4 w-4" />
                  ) : (
                    'Buy'
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <button
            onClick={() => {
              closeBuyModal();
              navigate('/billing');
            }}
            className="text-xs text-slate-500 hover:text-slate-800 underline"
            data-testid="buy-credits-view-billing"
          >
            View full billing page
          </button>
          <button
            onClick={closeBuyModal}
            className="text-xs text-slate-500 hover:text-slate-800"
            data-testid="buy-credits-close"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
